import { CheckCircle } from '@mui/icons-material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import PhoneEnabled from '@mui/icons-material/PhoneEnabled';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber, formatCreditCardNumber, formatIsoDateString } from '@/utils/strings';
import { useNavigate, useParams } from 'react-router-dom';
import { deviceType } from '@/utils/device';
import { useEffect } from 'react';
import Loading from '@/components/Loading/Loading';
import { useRegister } from '@/hooks/card/useRegister';

const CardConfirmation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { paymentSessionId: paymentSessionIdFromParams } = useParams();
  const { paymentSession, isLoadingPaymentSession, queryErrorPaymentSession, setPaymentSessionId } = useRegister();

  const small = window.innerWidth < 430;

  useEffect(() => {
    if (paymentSessionIdFromParams) {
      setPaymentSessionId(paymentSessionIdFromParams);
    }
  }, [paymentSessionIdFromParams]);

  useEffect(() => {
    if (queryErrorPaymentSession) {
      navigate('/card/error', { replace: true });
    }
  }, [queryErrorPaymentSession]);

  if (isLoadingPaymentSession || !paymentSession) {
    return <Loading height='50vh' />;
  }

  return (
    <Stack gap={4} alignItems='center' justifyContent='center'>
      <Typography variant='body1' fontWeight='bold'>
        {t('page.card.confirmation-step.title')}
      </Typography>

      <CheckCircle color='success' sx={{ fontSize: 64 }} />

      <Card>
        <CardHeader title={t('common-phrases.phone-number')} subheader={formatPhoneNumber(paymentSession.ani)} />
        <CardContent>
          <Stack gap={1}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='body2' fontWeight='bold'>
                {t('common-phrases.card-number')}
              </Typography>
              <Typography variant='body2'>
                {paymentSession.cardNumber ? formatCreditCardNumber(paymentSession.cardNumber) : ''}
              </Typography>
            </Stack>

            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='body2' fontWeight='bold'>
                {t('common-phrases.valid-to')}
              </Typography>
              <Typography variant='body2'>
                {paymentSession.cardValidThrough ? formatIsoDateString(paymentSession.cardValidThrough) : ''}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack gap={1} alignItems='center'>
        {paymentSession.lineName && paymentSession.accessNumber ? (
          <Stack direction={small ? 'column' : 'row'} spacing={0.5} justifyContent='center' alignItems='center'>
            <Typography variant='body2'>{t('page.card.confirmation-step.you-can-call-reference')}</Typography>
            <Typography variant='body2' fontWeight='bold'>
              {paymentSession.lineName}
            </Typography>
            {formatPhoneNumber(paymentSession.lineName) !== formatPhoneNumber(paymentSession.accessNumber) && (
              <>
                <Typography variant='body2'>{t('common-phrases.at')}</Typography>
                <Typography variant='body2' fontWeight='bold'>
                  {formatPhoneNumber(paymentSession.accessNumber)}
                </Typography>
              </>
            )}
          </Stack>
        ) : (
          <Typography variant='body2' align='center'>
            {t('page.card.confirmation-step.you-can-call')}
          </Typography>
        )}
        {deviceType === 'Mobile' && (
          <Button
            fullWidth
            variant='contained'
            color='primary'
            startIcon={<PhoneEnabled />}
            onClick={() => {
              window.location.href = `tel:${paymentSession.accessNumber}`;
            }}
          >
            {t('common-phrases.call-now')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default CardConfirmation;
